import React from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { BookOpen, FileText } from "lucide-react"

import { cn } from "@/lib/utils"
import { CopyButton } from "@/components/ui/copy-button"

// Process text to render @mentions as styled chips
function renderMentionChips(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") return child

    // Match @file.ext or @Multi Word Capitalized Name
    const mentionRegex =
      /@([\w\-._ ()]+\.(pdf|docx?|txt|md|xlsx?))|@([A-Z][\w]*(?:\s[A-Z][\w]*)*)/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    while ((match = mentionRegex.exec(child)) !== null) {
      if (match.index > lastIndex) {
        parts.push(child.slice(lastIndex, match.index))
      }
      const isFile = !!match[1]
      parts.push(
        <span
          key={`m-${match.index}`}
          className="inline-flex items-center gap-1 bg-brand-light text-brand rounded-md px-1.5 py-0.5 text-[0.9em] font-medium align-baseline border border-brand/20 mx-0.5"
        >
          {isFile ? (
            <FileText className="h-3 w-3" />
          ) : (
            <BookOpen className="h-3 w-3" />
          )}
          {match[0].slice(1)}
        </span>
      )
      lastIndex = match.index + match[0].length
    }

    if (lastIndex === 0) return child // no mentions
    if (lastIndex < child.length) {
      parts.push(child.slice(lastIndex))
    }
    return <>{parts}</>
  })
}

interface MarkdownRendererProps {
  children: string
}

export function MarkdownRenderer({ children }: MarkdownRendererProps) {
  return (
    <div className="space-y-3">
      <Markdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {children}
      </Markdown>
    </div>
  )
}

interface CodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  children: React.ReactNode
  className?: string
  language: string
}

const CodeBlock = ({
  children,
  className,
  language,
  ...restProps
}: CodeBlockProps) => {
  const code =
    typeof children === "string"
      ? children
      : childrenTakeAllStringContents(children)

  const preClass = cn(
    "overflow-x-scroll rounded-md border bg-muted p-4 font-mono text-sm [scrollbar-width:none]",
    className
  )

  return (
    <div className="group/code relative mb-4">
      <pre className={preClass} {...restProps}>
        <code>{children}</code>
      </pre>

      <div className="invisible absolute right-2 top-2 flex space-x-1 rounded-lg p-1 opacity-0 transition-all duration-200 group-hover/code:visible group-hover/code:opacity-100">
        <CopyButton content={code} copyMessage="Copied code to clipboard" />
      </div>
    </div>
  )
}

function childrenTakeAllStringContents(element: any): string {
  if (typeof element === "string") {
    return element
  }

  if (element?.props?.children) {
    let children = element.props.children

    if (Array.isArray(children)) {
      return children
        .map((child) => childrenTakeAllStringContents(child))
        .join("")
    } else {
      return childrenTakeAllStringContents(children)
    }
  }

  return ""
}

const COMPONENTS = {
  h1: withClass("h1", "text-2xl font-semibold"),
  h2: withClass("h2", "font-semibold text-xl"),
  h3: withClass("h3", "font-semibold text-lg"),
  h4: withClass("h4", "font-semibold text-base"),
  h5: withClass("h5", "font-medium"),
  strong: withClass("strong", "font-semibold"),
  a: MarkdownLink,
  blockquote: withClass("blockquote", "border-l-2 border-primary pl-4"),
  code: ({ children, className, node, ...rest }: any) => {
    const match = /language-(\w+)/.exec(className || "")
    return match ? (
      <CodeBlock className={className} language={match[1]} {...rest}>
        {children}
      </CodeBlock>
    ) : (
      <code
        className={cn(
          "font-mono [:not(pre)>&]:rounded-md [:not(pre)>&]:bg-muted [:not(pre)>&]:px-1 [:not(pre)>&]:py-0.5"
        )}
        {...rest}
      >
        {children}
      </code>
    )
  },
  pre: ({ children }: any) => children,
  ol: withClass("ol", "list-decimal space-y-2 pl-6"),
  ul: withClass("ul", "list-disc space-y-2 pl-6"),
  li: ({ node, children, ...props }: any) => (
    <li className="my-1.5" {...props}>
      {renderMentionChips(children)}
    </li>
  ),
  table: withClass(
    "table",
    "w-full border-collapse overflow-y-auto rounded-md border border-foreground/20"
  ),
  th: withClass(
    "th",
    "border border-foreground/20 px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right"
  ),
  td: withClass(
    "td",
    "border border-foreground/20 px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right"
  ),
  tr: withClass("tr", "m-0 border-t p-0 even:bg-muted"),
  p: ({ node, children, ...props }: any) => (
    <p className="whitespace-pre-wrap" {...props}>
      {renderMentionChips(children)}
    </p>
  ),
  hr: withClass("hr", "border-foreground/20"),
}

// In-page anchors and mailto/tel links keep default behaviour; everything
// else opens in a new tab so users don't lose their chat.
function opensInNewTab(href?: string) {
  if (!href) return false
  return !/^(#|mailto:|tel:)/i.test(href)
}

export function MarkdownLink({ node, href, children, className, ...props }: any) {
  const newTab = opensInNewTab(href)
  return (
    <a
      href={href}
      className={cn("text-primary underline underline-offset-2", className)}
      {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
      {...props}
    >
      {children}
      {newTab && <span className="sr-only"> (opens in a new tab)</span>}
    </a>
  )
}

function withClass(Tag: keyof JSX.IntrinsicElements, classes: string) {
  const Component = ({ node, ...props }: any) => (
    <Tag className={classes} {...props} />
  )
  Component.displayName = Tag
  return Component
}

export default MarkdownRenderer
